import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';
import DocumentUpload from '../../pages/document-management-portal/components/DocumentUpload';
import DocumentList from '../../pages/document-management-portal/components/DocumentList';
import DocumentPreview from '../../pages/document-management-portal/components/DocumentPreview';
import { getAllDocuments } from '../../services/documentService';

/**
 * Document Management Modal
 * Modal overlay for managing program documents without navigation
 */
const DocumentManagementModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadDocuments();
    }
  }, [isOpen]);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const docs = await getAllDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    setShowUpload(false);
    loadDocuments();
  };

  const handleDocumentSelect = (doc) => {
    setSelectedDocument(doc);
  };

  const handleDocumentDeleted = () => {
    setSelectedDocument(null);
    loadDocuments();
  };

  const handleClose = () => {
    setSelectedDocument(null);
    setShowUpload(false);
    setSearchQuery('');
    onClose();
  };

  // Filter documents based on search
  const filteredDocuments = documents?.filter(doc =>
    doc?.file_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    doc?.file_type?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    doc?.processing_status?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  // Statistics
  const stats = {
    total: documents?.length || 0,
    completed: documents?.filter(d => d?.processing_status === 'completed')?.length || 0,
    processing: documents?.filter(d => d?.processing_status === 'processing')?.length || 0,
    failed: documents?.filter(d => d?.processing_status === 'failed')?.length || 0,
    totalChunks: documents?.reduce((sum, doc) => sum + (doc?.chunk_count || 0), 0)
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={handleClose}
      />
      {/* Modal Content */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
          <div 
            className="relative bg-background rounded-lg shadow-elevated w-full max-w-7xl max-h-[90vh] overflow-hidden animate-slide-in"
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-background border-b border-border px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 z-10">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-foreground">Document Management</h2>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Upload and manage program documents for the AI chatbot knowledge base
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => setShowUpload(true)}
                  className="flex items-center space-x-2 flex-1 sm:flex-none"
                  size="sm"
                >
                  <Icon name="Upload" size={18} />
                  <span>Upload</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={24} />
                </Button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Statistics Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Documents</p>
                      <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
                    </div>
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="FileText" size={24} className="text-primary" />
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Completed</p>
                      <p className="text-2xl font-bold text-success">{stats?.completed}</p>
                    </div>
                    <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                      <Icon name="CheckCircle" size={24} className="text-success" />
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Processing</p>
                      <p className="text-2xl font-bold text-warning">{stats?.processing}</p>
                    </div>
                    <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
                      <Icon name="Loader" size={24} className="text-warning" />
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Failed</p>
                      <p className="text-2xl font-bold text-error">{stats?.failed}</p>
                    </div>
                    <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center">
                      <Icon name="XCircle" size={24} className="text-error" />
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Chunks</p>
                      <p className="text-2xl font-bold text-foreground">{stats?.totalChunks}</p>
                    </div>
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Database" size={24} className="text-primary" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative mb-4 sm:mb-6">
                <Icon 
                  name="Search" 
                  size={20} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder="Search documents by name, type, or status..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Main Content Area */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Document List */}
                <div className="lg:col-span-2">
                  {error && (
                    <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-4 flex items-center space-x-2">
                      <Icon name="AlertTriangle" size={20} className="text-error" />
                      <p className="text-error text-sm">{error}</p>
                    </div>
                  )}

                  <DocumentList
                    documents={filteredDocuments}
                    isLoading={isLoading}
                    onDocumentSelect={handleDocumentSelect}
                    selectedDocument={selectedDocument}
                    onRefresh={loadDocuments}
                  />
                </div>

                {/* Document Preview Panel */}
                <div className="lg:col-span-1">
                  {selectedDocument ? (
                    <DocumentPreview
                      document={selectedDocument}
                      onClose={() => setSelectedDocument(null)}
                      onDeleted={handleDocumentDeleted}
                      onRefresh={loadDocuments}
                    />
                  ) : (
                    <div className="bg-card border border-border rounded-lg p-8 text-center">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon name="FileText" size={32} className="text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground">
                        Select a document to view details
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Upload Modal */}
      {showUpload && (
        <DocumentUpload
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
          userId={user?.id}
        />
      )}
    </>
  );
};

export default DocumentManagementModal;